import {
  Box,
  Grid,
  LinearProgress,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Field, useFormikContext } from 'formik';
import { Checkbox, TextField } from 'formik-mui';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormSubmitButton } from '../../../components/Form';
import AppIcon, { AppIconName } from '../../../components/AppIcon';
import usePostHogCapture from '../../../hooks/usePostHogCapture';
import { redirectPage, sleep } from '../../../utils';
import { AuthFormFields, AuthUIMode } from '..';

interface AuthUIProps {
  mode: AuthUIMode;
  changeMode: VoidFunction;
}

const getOidcUrl = (providerId: 'google' | 'linkedin' | 'microsoft') => {
  const configuredServerUrl = process.env.REACT_APP_ONELOGIN_SERVER_URL;
  if (configuredServerUrl) {
    return `${configuredServerUrl}/oidc/login/${providerId}`;
  }
  if (window.location.hostname === 'localhost') {
    return `http://localhost:3010/oidc/login/${providerId}`;
  }
  return `/oidc/login/${providerId}`;
};

const Root = styled(Grid)(({ theme }) => ({
  minHeight: '100vh',
})) as typeof Grid;

const BgGrid = styled(Grid)(({ theme }) => ({
  background: 'url("/assets/login-graphic.png") no-repeat',
  backgroundPositionX: '65%',
  backgroundPositionY: 0,
  backgroundSize: 'cover',
  backgroundColor: theme.palette.surface.primary3,
  [theme.breakpoints.down('md')]: {
    order: -1,
    height: 220,
  },
})) as typeof Grid;

const SignInWithTypography = styled(Typography)(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  '&:before,&:after': {
    content: '" "',
    height: 1,
    background: theme.palette.charcoal[200],
    flex: 1,
  },
  '&:before': {
    marginRight: theme.spacing(1),
  },
  '&:after': {
    marginLeft: theme.spacing(1),
  },
}));

const OIDCButton = styled(Button)({
  padding: '12px 20px',
});

const labels = {
  signup: {
    headerTitle: 'Create your account',
    mobileHeaderTitle: 'Sign up',
    subtitle: 'Enter your e-mail below to create your account.',
    mobileSubtitle: 'Enter your e-mail below to create your account.',
    bottomTitle: 'Already have an account?',
    bottomButtonText: 'Sign in',
    buttonText: 'Continue',
    oidcTitle: 'Or sign up with',
  },
  login: {
    headerTitle: 'Enter your e-mail',
    mobileHeaderTitle: 'Sign in',
    subtitle: 'Please enter your e-mail below.',
    mobileSubtitle: 'Enter your e-mail below to authenticate your account.',
    bottomTitle: `Don't have an account?`,
    bottomButtonText: 'Sign up',
    buttonText: 'Sign in',
    oidcTitle: 'Or sign in with',
  },
};

const PakkedLogo = styled(AppIcon)(({ theme }) => ({
  zIndex: 3,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%,-50%)',
  color: theme.palette.white.main,
  [theme.breakpoints.down('md')]: {
    width: 69,
    height: 68,
  },
}));

const EXCHANGE_TOKEN_URL = '/auth/login/exchange_token';

const AuthUI: React.FC<AuthUIProps> = ({ mode, changeMode }) => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const formik = useFormikContext<AuthFormFields>();
  const postHogCapture = usePostHogCapture();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const isOutlook = searchParams.has('outlook');
  const modeLabels = labels[mode];

  const [showWelcomeScreen, setShowWelcomeScreen] = useState(isOutlook);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const exchangeToken = async () => {
      const response = await fetch(EXCHANGE_TOKEN_URL, {
        method: 'POST',
        credentials: 'same-origin',
      });

      if (response.status === 200) {
        postHogCapture(
          'user_logged_in',
          {
            email: formik.values.email,
          },
          {
            send_instantly: true,
            skip_client_rate_limiting: true,
          },
        );
        const body = await response.json();
        await sleep(1000);
        redirectPage(body.redirectUri);
      }
    };

    exchangeToken().finally(() => {
      setIsReady(true);
    });
  }, [formik.values.email, postHogCapture]);

  if (!isReady) {
    return <LinearProgress />;
  }

  const welcomeBullets: { icon: AppIconName; text: string }[] = [
    { icon: 'pin', text: 'Move attachments easily to Pakked' },
    { icon: 'mail', text: 'Add email messages to existing projects' },
    { icon: 'edit', text: 'Turn important emails into new projects' },
  ];

  const welcomeScreen = (
    <Stack spacing={5}>
      <Stack spacing={2}>
        <Typography variant='h2' color='text.primary3' textAlign='center'>
          Welcome
        </Typography>
        <Typography variant='body1' color='text.primary2' textAlign='center'>
          Maximize your project productivity with Pakked for Outlook.
        </Typography>
      </Stack>
      <Stack spacing={2}>
        {welcomeBullets.map(({ icon, text }, index) => {
          return (
            <Stack key={index} spacing={1} direction='row' alignItems='center'>
              <AppIcon
                name={icon}
                width={24}
                height={24}
                sx={{ color: (theme) => theme.palette.text.primary3 }}
              />
              <Typography variant='body1' color='text.primary1'>
                {text}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <Button
        variant='contained'
        size='large'
        onClick={() => setShowWelcomeScreen(false)}
      >
        Get started
      </Button>
    </Stack>
  );

  const oidc = (
    <Stack
      direction={isSmall ? 'column' : 'row'}
      spacing={2.5}
      justifyContent='center'
    >
      <OIDCButton
        variant='outlined'
        startIcon={<AppIcon name='google' />}
        onClick={() => redirectPage(getOidcUrl('google'))}
        fullWidth
      >
        Google
      </OIDCButton>
      <OIDCButton
        variant='outlined'
        startIcon={<AppIcon name='linkedin' />}
        onClick={() => redirectPage(getOidcUrl('linkedin'))}
        fullWidth
      >
        LinkedIn
      </OIDCButton>
      <OIDCButton
        variant='outlined'
        startIcon={<AppIcon name='microsoft' />}
        onClick={() => redirectPage(getOidcUrl('microsoft'))}
        fullWidth
      >
        Microsoft
      </OIDCButton>
    </Stack>
  );

  const modeSwitch = (
    <Stack
      direction='row'
      textAlign='center'
      justifyContent='center'
      alignItems='center'
      spacing={1}
    >
      <Typography color={theme.palette.charcoal[700]}>
        {modeLabels.bottomTitle}
      </Typography>
      <Button variant='text' onClick={changeMode}>
        {modeLabels.bottomButtonText}
      </Button>
    </Stack>
  );

  const header = (
    <Stack textAlign='left' alignSelf='auto' spacing={isSmall ? 2 : 1}>
      <Typography variant={isSmall ? 'h4' : 'h1'} color={isSmall ? 'text.primary1' : 'text.primary2'}>
        {isSmall ? modeLabels.mobileHeaderTitle : modeLabels.headerTitle}
      </Typography>
      <Typography variant={isSmall ? 'body2' : 'body1'} color='text.secondary'>
        {isSmall ? modeLabels.mobileSubtitle : modeLabels.subtitle}
      </Typography>
    </Stack>
  );

  const authSection = (
    <Stack spacing={3} paddingTop={isSmall ? 3 : 5}>
      <Field
        name='email'
        placeholder='Enter your email'
        label='E-mail'
        component={TextField}
      />
      <FormSubmitButton label={modeLabels.buttonText} />
      <Stack direction='row' spacing={1.5}>
        <Field name='rememberMe' component={Checkbox} type='checkbox' />
        <Typography variant='body1'>Remember me</Typography>
      </Stack>
      {!isOutlook && (
        <>
          <SignInWithTypography
            variant='body1'
            color={theme.palette.charcoal[700]}
          >
            {modeLabels.oidcTitle}
          </SignInWithTypography>
          {oidc}
        </>
      )}
      {modeSwitch}
    </Stack>
  );

  return (
    <Root container direction={isSmall ? 'column' : 'row'}>
      <Grid
        container
        item
        justifyContent='center'
        alignItems='center'
        md={6}
        padding={{ xs: theme.spacing(5, 3), md: 0 }}
      >
        <Box maxWidth={isSmall ? undefined : 320} width='100%'>
          {showWelcomeScreen ? (
            welcomeScreen
          ) : (
            <>
              {header}
              {authSection}
            </>
          )}
        </Box>
      </Grid>
      <BgGrid item alignItems='center' md={6} position='relative'>
        <PakkedLogo name='pakkedLogo' width={217} height={212} />
      </BgGrid>
    </Root>
  );
};

export default AuthUI;
